/**
 * ApprovalSection Component
 * Displays order approval status and admin actions
 *
 * @author SAUBHAGYA Development Team
 * @since Phase 2 - Order Approval Workflow
 */

import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  CheckCircle2,
  XCircle,
  Clock,
  Mail,
  Shield,
  AlertTriangle,
  Loader2,
  History,
} from 'lucide-react';
import { toast } from 'sonner';
import { orderService } from '@/services/commerce/orderService';
import type { OrderDetails } from '@/types/order';

interface ApprovalSectionProps {
  order: OrderDetails;
  onApprovalSuccess?: () => void;
}

/**
 * Get approval status badge color and icon
 */
function getApprovalStatusConfig(status?: string) {
  switch (status) {
    case 'APPROVED':
      return {
        color: 'bg-green-100 text-green-800 border-green-200',
        icon: CheckCircle2,
        label: 'Approved',
      };
    case 'REJECTED':
      return {
        color: 'bg-red-100 text-red-800 border-red-200',
        icon: XCircle,
        label: 'Rejected',
      };
    case 'PENDING':
      return {
        color: 'bg-amber-100 text-amber-800 border-amber-200',
        icon: Clock,
        label: 'Pending Approval',
      };
    case 'CANCELLED':
      return {
        color: 'bg-gray-100 text-gray-800 border-gray-200',
        icon: XCircle,
        label: 'Cancelled',
      };
    default:
      return {
        color: 'bg-slate-100 text-slate-800 border-slate-200',
        icon: Clock,
        label: status || 'Not Applicable',
      };
  }
}

/**
 * Calculate time remaining until token expiry
 */
function getTimeRemaining(expiryDate?: string): { text: string; isExpired: boolean; isUrgent: boolean } {
  if (!expiryDate) {
    return { text: 'No expiry set', isExpired: false, isUrgent: false };
  }

  const now = new Date();
  const expiry = new Date(expiryDate);
  const diffMs = expiry.getTime() - now.getTime();

  if (diffMs <= 0) {
    return { text: 'Expired', isExpired: true, isUrgent: false };
  }

  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

  if (hours > 24) {
    const days = Math.floor(hours / 24);
    return { text: `${days}d ${hours % 24}h remaining`, isExpired: false, isUrgent: false };
  }

  if (hours > 0) {
    return { text: `${hours}h ${minutes}m remaining`, isExpired: false, isUrgent: hours < 6 };
  }

  return { text: `${minutes}m remaining`, isExpired: false, isUrgent: true };
}

export function ApprovalSection({ order, onApprovalSuccess }: ApprovalSectionProps) {
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');
  const [isApproving, setIsApproving] = useState(false);
  const [isResending, setIsResending] = useState(false);

  // Only show for orders that need approval
  const showApprovalSection =
    order.orderStatus === 'AWAITING_CUSTOMER_APPROVAL' ||
    order.orderStatus === 'DELIVERED' ||
    (order as any).approvalStatus;

  if (!showApprovalSection) {
    return null;
  }

  const approvalStatus = (order as any).approvalStatus || 'NOT_APPLICABLE';
  const statusConfig = getApprovalStatusConfig(approvalStatus);
  const StatusIcon = statusConfig.icon;
  const tokenExpiry = (order as any).approvalTokenExpiry;
  const timeRemaining = getTimeRemaining(tokenExpiry);

  const canApprove = order.orderStatus === 'AWAITING_CUSTOMER_APPROVAL';
  const canResendEmail = order.orderStatus === 'AWAITING_CUSTOMER_APPROVAL' && !timeRemaining.isExpired;

  /**
   * Handle admin approval
   */
  async function handleAdminApprove() {
    setIsApproving(true);
    try {
      await orderService.adminApproveDelivery(order.id, adminNotes || undefined);
      toast.success('Order approved successfully!', {
        description: 'The customer will receive the final invoice via email.',
      });
      setShowApproveDialog(false);
      setAdminNotes('');
      onApprovalSuccess?.();
    } catch (error: any) {
      toast.error('Failed to approve order', {
        description: error.message || 'Please try again.',
      });
    } finally {
      setIsApproving(false);
    }
  }

  /**
   * Handle resend approval email
   */
  async function handleResendEmail() {
    setIsResending(true);
    try {
      const response = await orderService.resendApprovalEmail(order.id);
      toast.success('Approval email resent!', {
        description: `Email sent to ${response.emailSentTo}. New expiry: ${new Date(response.newTokenExpiry).toLocaleString()}`,
      });
      onApprovalSuccess?.();
    } catch (error: any) {
      toast.error('Failed to resend email', {
        description: error.message || 'Please try again.',
      });
    } finally {
      setIsResending(false);
    }
  }

  return (
    <>
      <Card className="border-2 border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 shadow-lg">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg text-amber-900">
            <Shield className="h-5 w-5" />
            Delivery Approval
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Approval Status Badge */}
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-amber-800">Status</span>
            <Badge className={`${statusConfig.color} border`}>
              <StatusIcon className="h-3.5 w-3.5 mr-1" />
              {statusConfig.label}
            </Badge>
          </div>

          {/* Token Expiry (if pending) */}
          {approvalStatus === 'PENDING' && tokenExpiry && (
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-amber-800">Token Expires</span>
              <span
                className={`text-sm font-semibold ${
                  timeRemaining.isExpired
                    ? 'text-red-600'
                    : timeRemaining.isUrgent
                    ? 'text-orange-600'
                    : 'text-amber-700'
                }`}
              >
                {timeRemaining.isUrgent && !timeRemaining.isExpired && (
                  <AlertTriangle className="h-3.5 w-3.5 inline mr-1" />
                )}
                {timeRemaining.text}
              </span>
            </div>
          )}

          {/* Approved By (if approved) */}
          {approvalStatus === 'APPROVED' && (order as any).approvalApprovedByEmail && (
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-amber-800">Approved By</span>
              <span className="text-sm text-amber-700">
                {(order as any).approvalApprovedByEmail}
              </span>
            </div>
          )}

          {/* Approved At (if approved) */}
          {approvalStatus === 'APPROVED' && (order as any).approvalApprovedAt && (
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-amber-800">Approved At</span>
              <span className="text-sm text-amber-700">
                {new Date((order as any).approvalApprovedAt).toLocaleString()}
              </span>
            </div>
          )}

          {/* Action Buttons */}
          {canApprove && (
            <div className="flex flex-col gap-2 pt-2 border-t border-amber-200">
              <Button
                onClick={() => setShowApproveDialog(true)}
                className="w-full bg-green-600 hover:bg-green-700"
                disabled={isApproving}
              >
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Admin Approve
              </Button>

              {canResendEmail && (
                <Button
                  onClick={handleResendEmail}
                  variant="outline"
                  className="w-full border-amber-300 text-amber-800 hover:bg-amber-100"
                  disabled={isResending}
                >
                  {isResending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Mail className="h-4 w-4 mr-2" />
                  )}
                  Resend Approval Email
                </Button>
              )}
            </div>
          )}

          {/* View History Link */}
          <Button
            variant="ghost"
            size="sm"
            className="w-full text-amber-700 hover:text-amber-900 hover:bg-amber-100"
            onClick={() => toast.info('Approval history coming soon!')}
          >
            <History className="h-4 w-4 mr-2" />
            View Approval History
          </Button>
        </CardContent>
      </Card>

      {/* Admin Approve Dialog */}
      <Dialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-green-600" />
              Admin Approve Delivery
            </DialogTitle>
            <DialogDescription>
              Approve this order on behalf of the customer. The customer will receive the final
              invoice via email.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-sm text-amber-800">
                <strong>Order:</strong> {order.orderNumber}
              </p>
              <p className="text-sm text-amber-800">
                <strong>Customer:</strong> {order.buyer?.name || 'N/A'}
              </p>
              <p className="text-sm text-amber-800">
                <strong>Amount:</strong> ₹{order.totals?.grandTotal?.toLocaleString() || '0'}
              </p>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                Admin Notes (Optional)
              </label>
              <Textarea
                placeholder="Add any notes about this approval..."
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowApproveDialog(false)}
              disabled={isApproving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAdminApprove}
              className="bg-green-600 hover:bg-green-700"
              disabled={isApproving}
            >
              {isApproving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Approving...
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Confirm Approval
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
